import Cookies from 'js-cookie';
import { useCallback, useEffect, useMemo, useState } from 'react';

declare global {
  interface Window {
    Weglot: {
      options: {
        languages: { language_to: string }[];
        language_from: string;
      };
      getCurrentLang: () => string;
      getLanguageName: (lang: string) => string;
      switchTo: (lang: string) => void;
      on: (event: string, callback: (lang: string) => void) => void;
      off: (event: string, callback: (lang: string) => void) => void;
    };
  }
}

export function useWeglot() {
  const [languageOptions, setLanguageOptions] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState('');
  const [currentLanguageLabel, setCurrentLanguageLabel] = useState('');
  const [languagePreference, setLanguagePreference] = useState(
    Cookies.get('wg') || ''
  );
  const [isWeglotAvailable, setIsWeglotAvailable] = useState(true);
  const domain = useMemo(() => {
    return window.location.host.split('.').slice(-2).join('.');
  }, [window.location.host]);

  useEffect(() => {
    if (!window.Weglot) {
      console.error('Weglot is not loaded.');
      setIsWeglotAvailable(false);
      return;
    }

    if (!isWeglotAvailable) return;

    try {
      const availableLanguages = [
        ...window.Weglot.options.languages.map(lang => lang.language_to),
        window.Weglot.options.language_from
      ];

      const options = availableLanguages.map(lang => ({
        label: window.Weglot.getLanguageName(lang),
        value: lang
      }));

      setLanguageOptions(options);

      const currentLang = window.Weglot.getCurrentLang();
      setCurrentLanguage(currentLang);
      setCurrentLanguageLabel(window.Weglot.getLanguageName(currentLang));

      const handleLanguageChange = newLang => {
        setCurrentLanguage(newLang);
        setCurrentLanguageLabel(window.Weglot.getLanguageName(newLang));
      };

      window.Weglot.on('languageChanged', handleLanguageChange);

      return () => {
        window.Weglot.off('languageChanged', handleLanguageChange);
      };
    } catch (error) {
      console.error('Weglot initialization failed:', error);
      setIsWeglotAvailable(false);
    }
  }, [isWeglotAvailable]);

  const switchLanguage = useCallback(
    async newLanguage => {
      if (!window.Weglot || !isWeglotAvailable) return;

      try {
        await new Promise(resolve => {
          const handleLanguageChange = lang => {
            setCurrentLanguage(lang);
            setCurrentLanguageLabel(window.Weglot.getLanguageName(lang));
            setLanguagePreference(lang);
            Cookies.set('wg', lang, {
              domain: `.${domain}`,
              expires: 365,
              path: '/'
            });

            resolve(true);
            window.Weglot.off('languageChanged', handleLanguageChange);
          };

          window.Weglot.on('languageChanged', handleLanguageChange);
          window.Weglot.switchTo(newLanguage);
        });
      } catch (error) {
        console.error('Failed to switch language:', error);
      }
    },
    [isWeglotAvailable, setLanguagePreference]
  );

  const syncLanguageWithCookie = useCallback(() => {
    const savedLanguage = Cookies.get('wg');

    if (savedLanguage && savedLanguage !== currentLanguage) {
      switchLanguage(savedLanguage);
    }
  }, [currentLanguage, switchLanguage]);

  return {
    currentLanguage,
    currentLanguageLabel,
    isWeglotAvailable,
    languageOptions,
    languagePreference,
    switchLanguage,
    syncLanguageWithCookie
  };
}
